import { useEffect, useMemo, useState } from "react";
import HDDialog from "@/common/components/HDDialog";
import { useStripeProducts } from "@/state/hooks/useStripeProducts";
import { useCreateBillingCheckout, useBillingPlan } from "@/state/hooks/useBilling";
import { formatCurrencyWhole, applyAnnualDiscount } from "./billingUtils";
import type { PlanProduct } from "@/features/admin/checkout/types";
import type { AccountTier, BillingType } from "@common/utils/types";
import cn from "classnames";
import { BillingIntervalToggle } from "./BillingCardParts";
import { trackPLGEvent } from "@common/lib/plgTracking";

interface PlanUpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentTier: AccountTier | null;
    billingType: BillingType;
}

const TIER_ORDER: Record<string, number> = {
    scribe: 1,
    assistant: 2,
    advisor: 3,
};

const DEFAULT_INCLUDED_VISITS: Record<string, number> = {
    scribe: 150,
    assistant: 400,
    advisor: 600,
};

const TIER_STYLES: Record<
    string,
    {
        accent: string;
        accentBg: string;
        accentBorder: string;
        checkBg: string;
        checkColor: string;
        btnFilled: string;
        btnOutline: string;
        popularBorder: string;
        popularShadow: string;
        popularBadgeBg: string;
    }
> = {
    scribe: {
        accent: "text-max-900",
        accentBg: "bg-max-25",
        accentBorder: "border-max-100",
        checkBg: "bg-max-50",
        checkColor: "text-max-600",
        btnFilled: "bg-max-600 hover:bg-max-900 text-white",
        btnOutline: "border-2 border-max-400 text-max-900 hover:bg-max-50 bg-white",
        popularBorder: "border-max-500",
        popularShadow: "shadow-[0_8px_32px_rgba(46,222,229,0.15)]",
        popularBadgeBg: "bg-gradient-to-r from-max-500 to-max-900",
    },
    assistant: {
        accent: "text-tropicana-600",
        accentBg: "bg-tropicana-25",
        accentBorder: "border-tropicana-100",
        checkBg: "bg-tropicana-50",
        checkColor: "text-tropicana-600",
        btnFilled: "bg-tropicana-500 hover:bg-tropicana-600 text-white shadow-md shadow-tropicana-200",
        btnOutline: "border-2 border-tropicana-400 text-tropicana-600 hover:bg-tropicana-50 bg-white",
        popularBorder: "border-tropicana-500",
        popularShadow: "shadow-[0_8px_32px_rgba(252,183,24,0.15)]",
        popularBadgeBg: "bg-gradient-to-r from-tropicana-500 to-tropicana-600",
    },
    advisor: {
        accent: "text-blaze-500",
        accentBg: "bg-blaze-25",
        accentBorder: "border-blaze-100",
        checkBg: "bg-blaze-50",
        checkColor: "text-blaze-500",
        btnFilled: "bg-blaze-500 hover:bg-blaze-600 text-white",
        btnOutline: "border-2 border-blaze-400 text-blaze-600 hover:bg-blaze-25 bg-white",
        popularBorder: "border-blaze-500",
        popularShadow: "shadow-[0_8px_32px_rgba(254,41,98,0.15)]",
        popularBadgeBg: "bg-gradient-to-r from-blaze-400 to-blaze-600",
    },
};

// Features per tier, using "Everything in X plus:" pattern
const TIER_FEATURES: Record<string, { inherits?: string; features: string[] }> = {
    scribe: {
        features: ["AI Scribe incl. PDF upload", "+$49/mo PIMS Integration", "Chat & Email Support"],
    },
    assistant: {
        inherits: "HappyScribe",
        features: ["PIMS Integration included", "Scout AI Assistant", "Call Capture", "Chat, Email & Video Support"],
    },
    advisor: {
        inherits: "HappyAssistant",
        features: ["Dedicated CSM", "Monthly Insights Report"],
    },
};

const PEOPLE_ICON = (
    <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0"
    >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

interface TierGroup {
    tier: string;
    displayName: string;
    monthlyPrice: PlanProduct | null;
    annualPrice: PlanProduct | null;
    includedVisits: number;
    order: number;
}

function detectTier(product: PlanProduct): string | null {
    const meta = product as PlanProduct & { metadata?: Record<string, string> };
    if (meta.metadata?.tier) return meta.metadata.tier.toLowerCase();

    const name = product.name.toLowerCase();
    if (name.includes("scribe")) return "scribe";
    if (name.includes("assistant")) return "assistant";
    if (name.includes("advisor")) return "advisor";
    return null;
}

function groupPlansByTier(plans: PlanProduct[]): TierGroup[] {
    const groups = new Map<string, TierGroup>();

    for (const plan of plans) {
        const tier = detectTier(plan);
        if (!tier || !(tier in TIER_ORDER)) continue;

        if (!groups.has(tier)) {
            groups.set(tier, {
                tier,
                displayName: `Happy${tier.charAt(0).toUpperCase() + tier.slice(1)}`,
                monthlyPrice: null,
                annualPrice: null,
                includedVisits: plan.included_visits || DEFAULT_INCLUDED_VISITS[tier] || 0,
                order: TIER_ORDER[tier],
            });
        }

        const group = groups.get(tier)!;

        if (plan.interval === "year") {
            group.annualPrice = plan;
        } else {
            group.monthlyPrice = plan;
        }

        if (plan.included_visits > 0) {
            group.includedVisits = plan.included_visits;
        }
    }

    return Array.from(groups.values()).sort((a, b) => a.order - b.order);
}

/** Compute annual savings based on the dynamic discount percentage from the backend. */
function calcSavings(group: TierGroup, discountPct: number): { percent: number; dollars: number } | null {
    if (!group.monthlyPrice || !group.annualPrice || discountPct <= 0) return null;
    const annualTotal = group.annualPrice.unit_amount;
    if (annualTotal <= 0) return null;
    const discountDollars = Math.round((annualTotal * discountPct) / 100);
    if (discountDollars <= 0) return null;
    return {
        percent: discountPct,
        dollars: discountDollars,
    };
}

export default function PlanUpgradeModal({
    isOpen,
    onClose,
    currentTier,
    billingType,
}: PlanUpgradeModalProps): React.JSX.Element {
    const { data: catalog } = useStripeProducts({ enabled: isOpen });
    const checkout = useCreateBillingCheckout();
    const { data: billingPlan } = useBillingPlan({ enabled: isOpen });
    const isCurrentlyAnnual = billingPlan?.billing_interval === "year";
    const [billingInterval, setBillingInterval] = useState<"month" | "year">(isCurrentlyAnnual ? "year" : "month");
    const [pendingTier, setPendingTier] = useState<string | null>(null);

    // If the user is on an annual plan, force the interval to "year"
    useEffect(() => {
        if (isCurrentlyAnnual) setBillingInterval("year");
    }, [isCurrentlyAnnual]);

    // Track plans viewed when modal opens (once per session)
    useEffect(() => {
        if (isOpen) {
            if (sessionStorage.getItem("plg_plans_viewed_fired")) return;
            sessionStorage.setItem("plg_plans_viewed_fired", "true");
            trackPLGEvent("plans_viewed");
        }
    }, [isOpen]);

    const isTrial = billingType === "trial";
    const annualDiscountPct = catalog?.annual_discount_percent ?? 0;
    const allTierGroups = useMemo(() => (catalog ? groupPlansByTier(catalog.plans) : []), [catalog]);
    const currentTierOrder = currentTier ? (TIER_ORDER[currentTier] ?? 0) : 0;

    const tierGroups = isTrial ? allTierGroups : allTierGroups.filter((g) => g.order >= currentTierOrder);

    const nextUpgradeGroup = tierGroups.find((g) => g.tier !== currentTier);
    // For trial users, highlight assistant (Most Popular) so the prominent CTA matches the card we emphasize. For paid users, highlight the first upgrade tier.
    const prominentTier = (() => {
        if (isTrial && tierGroups.some((g) => g.tier === "assistant")) return "assistant";
        if (nextUpgradeGroup) return nextUpgradeGroup.tier;
        if (tierGroups.some((g) => g.tier === "assistant")) return "assistant";
        return tierGroups[tierGroups.length - 1]?.tier ?? null;
    })();

    function handleUpgrade(group: TierGroup): void {
        const selectedPrice = billingInterval === "year" ? group.annualPrice : group.monthlyPrice;
        const priceToUse = selectedPrice ?? group.monthlyPrice ?? group.annualPrice;
        if (!priceToUse) return;

        trackPLGEvent("checkout_initiated", {
            plan: group.tier,
            billing_cadence: billingInterval,
        });

        setPendingTier(group.tier);
        checkout.mutate(
            {
                price_id: priceToUse.stripe_price_id,
                mode: isTrial ? "new_subscription" : "upgrade",
            },
            { onSettled: () => setPendingTier(null) },
        );
    }

    const isAnnual = billingInterval === "year";

    // Pre-compute savings for the toggle badge
    const maxAnnualSavings = useMemo(() => {
        const percents = tierGroups.map((g) => calcSavings(g, annualDiscountPct)?.percent ?? 0);
        return Math.max(0, ...percents);
    }, [tierGroups, annualDiscountPct]);

    return (
        <HDDialog
            isOpen={isOpen}
            handleClose={onClose}
            header={isTrial ? "Choose Your Plan" : "Upgrade Your Plan"}
            className="!w-[95%] sm:!max-w-4xl"
            hideContentFooterBorder
        >
            {tierGroups.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No plans available. Please try again later.</p>
            ) : (
                <div className="flex flex-col gap-4 pb-2">
                    {/* Monthly/Annual toggle — hidden for annual subscribers */}
                    {!isCurrentlyAnnual && (
                        <BillingIntervalToggle
                            value={billingInterval}
                            onChange={setBillingInterval}
                            trailingBadge={
                                maxAnnualSavings > 0 ? (
                                    <span className="absolute left-full ml-2 text-xs font-bold text-max-900 bg-max-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                                        Save up to {maxAnnualSavings}%
                                    </span>
                                ) : undefined
                            }
                        />
                    )}

                    {/* Unlimited users banner */}
                    <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-max-25 via-max-50 to-max-25 border border-max-100 rounded-lg py-2.5 px-4">
                        <span className="text-max-900">{PEOPLE_ICON}</span>
                        <span className="text-sm font-semibold text-max-900">
                            All plans include <strong>unlimited users</strong> &mdash; no per-seat fees, ever
                        </span>
                    </div>

                    {/* Plan cards */}
                    <div className="flex gap-4 items-stretch justify-center mt-2">
                        {tierGroups.map((group) => {
                            const style = TIER_STYLES[group.tier] ?? TIER_STYLES.scribe;
                            const isCurrentPlan = !isTrial && currentTier === group.tier;
                            const isMostPopular = group.tier === "assistant";
                            const isProminent = group.tier === prominentTier && !isCurrentPlan;
                            const isLoading = pendingTier === group.tier && checkout.isPending;
                            const tierFeatures = TIER_FEATURES[group.tier];

                            // Pre-compute prices and savings once per card
                            const savings = calcSavings(group, annualDiscountPct);
                            const monthlyPriceLabel = group.monthlyPrice
                                ? formatCurrencyWhole(group.monthlyPrice.unit_amount)
                                : null;
                            const discountedAnnual = group.annualPrice
                                ? applyAnnualDiscount(group.annualPrice.unit_amount, annualDiscountPct, "year")
                                : null;
                            const displayPrice =
                                isAnnual && discountedAnnual != null
                                    ? formatCurrencyWhole(Math.round(discountedAnnual / 12))
                                    : (monthlyPriceLabel ?? "--");
                            const dollarSavingsLabel = savings ? formatCurrencyWhole(savings.dollars) : null;

                            return (
                                <div
                                    key={group.tier}
                                    className={cn(
                                        "flex-1 max-w-[280px] rounded-xl flex flex-col relative",
                                        isMostPopular
                                            ? cn("border-2", style.popularBorder, style.popularShadow, "scale-[1.02]")
                                            : "border border-gray-200",
                                    )}
                                >
                                    {/* Most Popular badge */}
                                    {isMostPopular && (
                                        <div
                                            className={cn(
                                                "absolute -top-3.5 left-1/2 -translate-x-1/2 text-white text-[11px] font-extrabold tracking-wide uppercase px-4 py-1 rounded-full whitespace-nowrap shadow-sm",
                                                style.popularBadgeBg,
                                            )}
                                        >
                                            &#9733; Most Popular
                                        </div>
                                    )}

                                    <div className="p-6 flex flex-col flex-1">
                                        {/* Tier badge + name */}
                                        <div className="text-center mb-4">
                                            {isCurrentPlan && (
                                                <span
                                                    className={cn(
                                                        "inline-block text-[11px] font-semibold px-3 py-1 rounded-full mb-2 border",
                                                        style.accent,
                                                        style.accentBg,
                                                        style.accentBorder,
                                                    )}
                                                >
                                                    Your Plan
                                                </span>
                                            )}
                                            {!isCurrentPlan && <div className="h-4" />}
                                            <div className="text-xl font-bold text-gray-900">{group.displayName}</div>
                                        </div>

                                        {/* Price */}
                                        <div className="text-center mb-1">
                                            {isAnnual && monthlyPriceLabel && (
                                                <div className="text-sm text-gray-400 line-through mb-0.5">
                                                    {monthlyPriceLabel}/mo
                                                </div>
                                            )}
                                            <span
                                                className={cn(
                                                    "text-[40px] font-extrabold tracking-tight",
                                                    style.accent,
                                                )}
                                            >
                                                {displayPrice}
                                            </span>
                                            <span className="text-sm text-gray-500 ml-0.5">/mo</span>
                                            {isAnnual && (
                                                <div className="text-[11px] text-gray-500 mt-0.5">billed annually</div>
                                            )}
                                        </div>

                                        {/* Savings + visits under price */}
                                        <div className="text-center mb-5">
                                            {isAnnual && dollarSavingsLabel && (
                                                <span className="inline-flex items-center gap-1 bg-tropicana-50 border border-tropicana-100 text-tropicana-700 text-[11px] font-bold px-2.5 py-0.5 rounded-full mb-1.5">
                                                    &#9733; You save {dollarSavingsLabel}/yr
                                                </span>
                                            )}
                                            <div className={cn("text-sm font-bold", style.accent)}>
                                                Includes {group.includedVisits} visits/mo
                                            </div>
                                            <div className="flex items-center justify-center gap-1 text-xs font-semibold text-gray-500 mt-0.5">
                                                {PEOPLE_ICON}
                                                Unlimited users
                                            </div>
                                        </div>

                                        {/* Features */}
                                        <div className="flex-1 flex flex-col gap-2 mb-6">
                                            {tierFeatures?.inherits && (
                                                <p className="text-xs font-semibold text-gray-500 mb-1">
                                                    Everything in {tierFeatures.inherits} plus:
                                                </p>
                                            )}
                                            {tierFeatures?.features.map((feature) => {
                                                const isAddon = feature.startsWith("+");
                                                const isDisabled = feature.startsWith("\u2014");
                                                return (
                                                    <div
                                                        key={feature}
                                                        className={cn(
                                                            "flex items-center gap-2 text-[13px]",
                                                            isDisabled && "text-gray-300",
                                                        )}
                                                    >
                                                        {isAddon ? (
                                                            <span
                                                                className={cn(
                                                                    "text-[11px] font-bold px-2 py-0.5 rounded-md shrink-0",
                                                                    style.accentBg,
                                                                    style.accent,
                                                                )}
                                                            >
                                                                {feature.split(" ")[0]}
                                                            </span>
                                                        ) : isDisabled ? (
                                                            <span className="w-5 text-center shrink-0">&mdash;</span>
                                                        ) : (
                                                            <span
                                                                className={cn(
                                                                    "w-5 h-5 rounded-full flex items-center justify-center text-xs shrink-0",
                                                                    style.checkBg,
                                                                    style.checkColor,
                                                                )}
                                                            >
                                                                &#10003;
                                                            </span>
                                                        )}
                                                        <span>
                                                            {isAddon ? feature.split(" ").slice(1).join(" ") : feature}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* CTA */}
                                        {isCurrentPlan ? (
                                            <span className="text-center text-xs font-medium text-gray-400 py-2.5">
                                                Your current plan
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                className={cn(
                                                    "w-full rounded-lg text-sm font-bold py-3 px-4 transition-all",
                                                    "disabled:opacity-50 disabled:cursor-not-allowed",
                                                    isProminent ? style.btnFilled : style.btnOutline,
                                                )}
                                                onClick={() => handleUpgrade(group)}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? "Redirecting..." : isTrial ? "Get Started" : "Upgrade Now"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </HDDialog>
    );
}
